import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useSubjectLabel } from './subject-labels';
import { SubjectDot } from './subjects';
import {
  isEveryone,
  type PageCountDto,
  type Subject,
  type SubjectPagesDto,
} from './usage-analytics.api';
import { formatRate, formatShare, ratio, usePageLabel } from './usage-format';

/**
 * What the top-pages table cannot show.
 *
 * Ranked by raw share, every subject's list is led by the same handful of
 * pages everybody uses. These two panels subtract that out: one against
 * everybody else, one against the subject's own earlier self.
 */

/** Rows per list. Enough to see a pattern, few enough to read at a glance. */
const LIST_SIZE = 5;

/**
 * Fewer visits than this to a page and its share is noise.
 *
 * One visit out of twelve is 8%, and against a baseline of 1% that is an
 * eightfold "preference" built on a single click.
 */
const MIN_PAGE_VISITS = 5;

/** A subject with less than this in the range has no pattern to speak of yet. */
const MIN_SUBJECT_VISITS = 20;

/** How far from the baseline a share has to sit before it is called different. */
const OVER = 1.5;
const UNDER = 1 / OVER;

/** A page has to matter to everybody before a subject ignoring it is a finding. */
const MIN_BASELINE_SHARE = 0.02;

const pageId = (page: { domain: string; page: string }) =>
  `${page.domain}|${page.page}`;

interface InsightRow {
  id: string;
  label: string;
  /** The headline: a multiple, or a change in points. */
  figure: string;
  /** What the headline was worked out from. */
  detail: string;
}

function InsightList({
  title,
  icon,
  rows,
  empty,
}: {
  title: string;
  icon: ReactNode;
  rows: InsightRow[];
  empty: string;
}) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-muted-foreground flex items-center gap-1 text-xs font-medium">
        {icon}
        {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((row) => (
            <li key={row.id} className="flex items-baseline gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate">{row.label}</span>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                {row.detail}
              </span>
              <span className="w-16 shrink-0 text-right font-medium tabular-nums">
                {row.figure}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InsightCard({
  subject,
  note,
  children,
}: {
  subject: Subject;
  /** Shown in place of the lists when there is nothing to work them out from. */
  note?: string;
  children?: ReactNode;
}) {
  const label = useSubjectLabel();
  return (
    <div className="bg-card space-y-3 rounded-xl border p-3">
      <span className="flex min-w-0 items-center gap-2">
        <SubjectDot subject={subject} />
        <span className="truncate text-sm font-medium">{label(subject)}</span>
      </span>
      {note ? (
        <p className="text-muted-foreground text-sm">{note}</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">{children}</div>
      )}
    </div>
  );
}

/**
 * Pages a subject uses more, or less, than everybody.
 *
 * `baseline` is everybody under the same range, app and store — which includes
 * the subject. That understates every difference a little, and for a subject
 * that is most of the traffic it understates it a lot; it is still the honest
 * comparison, because "everyone else" is a different denominator for every
 * subject on the page and the multiples would stop being comparable.
 */
export function DistinctivePages({
  subjects,
  pages,
  baseline,
  top,
}: {
  subjects: Subject[];
  pages: Map<string, SubjectPagesDto>;
  baseline: SubjectPagesDto | undefined;
  /** How many pages each list was asked for: a list that long may have been cut. */
  top: number;
}) {
  const { t } = useTranslation();
  const pageLabel = usePageLabel();

  const base = useMemo(
    () => new Map((baseline?.pages ?? []).map((page) => [pageId(page), page])),
    [baseline]
  );

  // Everybody compared with everybody is 1.0× all the way down.
  const compared = subjects.filter((subject) => !isEveryone(subject));
  if (compared.length === 0 || !baseline) return null;

  return (
    <div className="grid gap-3 xl:grid-cols-2">
      {compared.map((subject) => {
        const own = pages.get(subject.key);
        if (!own || own.totalVisits < MIN_SUBJECT_VISITS) {
          return (
            <InsightCard
              key={subject.key}
              subject={subject}
              note={t('usage.tooFewVisits', { count: MIN_SUBJECT_VISITS })}
            />
          );
        }

        const mine = new Map(own.pages.map((page) => [pageId(page), page]));
        // A full list may have stopped short of a page the subject did open,
        // and "never" is too strong a word to print on a guess.
        const cut = own.pages.filter((page) => page.visits > 0).length >= top;
        const shareOf = (page: PageCountDto | undefined, total: number) =>
          ratio(page?.visits ?? 0, total);

        const more: (InsightRow & { lift: number })[] = [];
        for (const page of own.pages) {
          if (page.visits < MIN_PAGE_VISITS) continue;
          const everyone = base.get(pageId(page));
          // Not in the baseline's list means the list was cut before reaching
          // it, not that nobody opened it: there is no share to divide by.
          if (!everyone || everyone.visits === 0) continue;
          const share = shareOf(page, own.totalVisits);
          const baseShare = shareOf(everyone, baseline.totalVisits);
          const lift = ratio(share, baseShare);
          if (lift < OVER) continue;
          more.push({
            id: pageId(page),
            label: pageLabel(page.domain, page.page),
            figure: `${formatRate(lift)}×`,
            detail: `${formatShare(share)} · ${formatShare(baseShare)}`,
            lift,
          });
        }
        more.sort((a, b) => b.lift - a.lift);

        const less: (InsightRow & { lift: number })[] = [];
        for (const everyone of baseline.pages) {
          const baseShare = shareOf(everyone, baseline.totalVisits);
          if (everyone.visits < MIN_PAGE_VISITS || baseShare < MIN_BASELINE_SHARE) {
            continue;
          }
          const page = mine.get(pageId(everyone));
          if (!page && cut) continue;
          const share = shareOf(page, own.totalVisits);
          const lift = ratio(share, baseShare);
          if (lift > UNDER) continue;
          less.push({
            id: pageId(everyone),
            label: pageLabel(everyone.domain, everyone.page),
            figure: share === 0 ? t('usage.never') : `${formatRate(lift)}×`,
            detail: `${formatShare(share)} · ${formatShare(baseShare)}`,
            lift,
          });
        }
        less.sort((a, b) => a.lift - b.lift);

        return (
          <InsightCard key={subject.key} subject={subject}>
            <InsightList
              title={t('usage.usesMore')}
              icon={<ArrowUp className="size-3" />}
              rows={more.slice(0, LIST_SIZE)}
              empty={t('usage.nothingStandsOut')}
            />
            <InsightList
              title={t('usage.usesLess')}
              icon={<ArrowDown className="size-3" />}
              rows={less.slice(0, LIST_SIZE)}
              empty={t('usage.nothingStandsOut')}
            />
          </InsightCard>
        );
      })}
    </div>
  );
}

/**
 * Pages whose share of a subject's visits moved most since the span before.
 *
 * Measured in share and not in visits: a subject that was simply busier this
 * month has more visits to everything, and a list of "rising" pages that is
 * really a list of all of them says nothing about where attention went.
 */
export function PageMovers({
  subjects,
  pages,
}: {
  subjects: Subject[];
  pages: Map<string, SubjectPagesDto>;
}) {
  const { t } = useTranslation();
  const pageLabel = usePageLabel();

  return (
    <div className="grid gap-3 xl:grid-cols-2">
      {subjects.map((subject) => {
        const own = pages.get(subject.key);
        const before = own?.previousTotalVisits ?? null;

        if (!own || before === null) {
          return (
            <InsightCard
              key={subject.key}
              subject={subject}
              note={t('usage.noEarlierPeriod')}
            />
          );
        }
        if (own.totalVisits < MIN_SUBJECT_VISITS || before < MIN_SUBJECT_VISITS) {
          return (
            <InsightCard
              key={subject.key}
              subject={subject}
              note={t('usage.tooFewVisitsBoth', { count: MIN_SUBJECT_VISITS })}
            />
          );
        }

        const moved = own.pages
          .filter(
            (page) =>
              Math.max(page.visits, page.previousVisits ?? 0) >= MIN_PAGE_VISITS
          )
          .map((page) => {
            const now = ratio(page.visits, own.totalVisits);
            const then = ratio(page.previousVisits ?? 0, before);
            const points = (now - then) * 100;
            return {
              id: pageId(page),
              label: pageLabel(page.domain, page.page),
              figure: `${points > 0 ? '+' : '−'}${Math.abs(points).toFixed(1)}`,
              detail: `${formatShare(then)} → ${formatShare(now)}`,
              points,
            };
          })
          // Under a tenth of a point rounds to "+0.0", which is not a move.
          .filter((row) => Math.abs(row.points) >= 0.1);

        const rising = moved
          .filter((row) => row.points > 0)
          .sort((a, b) => b.points - a.points);
        const falling = moved
          .filter((row) => row.points < 0)
          .sort((a, b) => a.points - b.points);

        return (
          <InsightCard key={subject.key} subject={subject}>
            <InsightList
              title={t('usage.rising')}
              icon={<ArrowUp className="size-3" />}
              rows={rising.slice(0, LIST_SIZE)}
              empty={t('usage.nothingMoved')}
            />
            <InsightList
              title={t('usage.falling')}
              icon={<ArrowDown className="size-3" />}
              rows={falling.slice(0, LIST_SIZE)}
              empty={t('usage.nothingMoved')}
            />
          </InsightCard>
        );
      })}
    </div>
  );
}
